"use client";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";

type DropdownProps = {
  locale: Locales;
  title: string;
  unit?: string;
  defaultValue?: string | number;
  onChange?: (value: string) => void;
  className?: string;
} & (
  | {
      min: number;
      max: number;
      step: number;
      values?: never;
    }
  | {
      values: string;
      min?: never;
      max?: never;
      step?: never;
    }
);

const Dropdown = ({ locale, title, unit = "", defaultValue, onChange, className, ...props }: DropdownProps) => {
  const generateOptions = (): string[] => {
    if ("values" in props && props.values) {
      return props.values
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    } else if ("min" in props && props.min !== undefined) {
      const options: string[] = [];
      for (let i = props.min; i <= props.max; i += props.step) {
        options.push(i.toString());
      }
      return options;
    }
    return [];
  };

  const options = generateOptions();

  const handleValueChange = (value: string) => {
    if (onChange) {
      onChange(value);
    }
  };

  if (options.length === 0) {
    return null; // Don't render if no options available
  }

  return (
    <div className={cn("space-y-2", className)}>
      <label className="text-dark-primary font-cabinet-grotesk text-sm font-medium">{title}</label>
      <Select key={defaultValue || "reset"} onValueChange={handleValueChange} defaultValue={defaultValue?.toString()}>
        <SelectTrigger className="border-border-light font-cabinet-grotesk aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 data-[placeholder]:text-dark-primary text-dark-primary h-10 bg-white text-sm font-bold placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px] [&>svg]:size-5">
          <SelectValue placeholder={__("Select a value", locale)} />
        </SelectTrigger>
        <SelectContent className="font-cabinet-grotesk max-h-[300px]">
          {options.map((option) => (
            <SelectItem key={option} className="cursor-pointer" value={option}>
              {option}
              {unit ? ` ${unit}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default Dropdown;
